/**
 * Parental gate state (MONETIZATION-PLAN P0-3), shared by every grown-up-only surface: the paywall,
 * the grown-ups corner, onboarding, Settings links, external links (Terms, Privacy, store pages, gift page).
 *
 *  - Challenge: randomized two-digit addition (e.g. 37 + 48), also written out in words in the parent's
 *    language, answered on a number pad. A 4-8 year-old is unlikely to solve it or guess it.
 *  - Rate limit: 3 wrong answers → 60 s lockout, doubling each time (60, 120, 240 … max 15 min).
 *    The lockout survives a reload (localStorage); a pass resets it.
 *  - Once passed, the gate stays open 10 minutes (sessionStorage, this app session only).
 */
import type { Lang } from '@sketchling/core';

const OPEN_KEY = 'sk-parents-until';
const LOCK_KEY = 'sk-pgate-lock';
export const GATE_OPEN_MS = 10 * 60_000;
export const GATE_MAX_TRIES = 3;
const LOCK_BASE_MS = 60_000;
const LOCK_MAX_MS = 15 * 60_000;

export function gateOpen(): boolean {
  try {
    return Number(sessionStorage.getItem(OPEN_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

export function openGate(): void {
  try {
    sessionStorage.setItem(OPEN_KEY, String(Date.now() + GATE_OPEN_MS));
  } catch {
    /* private mode: the gate just asks again */
  }
}

export function closeGate(): void {
  try {
    sessionStorage.removeItem(OPEN_KEY);
  } catch {
    /* ignore */
  }
}

export interface GateLock {
  /** Wrong answers since the last pass / lockout. */
  fails: number;
  /** Lockouts so far (escalates the next lockout). */
  level: number;
  /** Locked until (epoch ms), 0 = not locked. */
  until: number;
}

export function readLock(): GateLock {
  try {
    const v = JSON.parse(localStorage.getItem(LOCK_KEY) ?? 'null') as Partial<GateLock> | null;
    return { fails: Number(v?.fails ?? 0), level: Number(v?.level ?? 0), until: Number(v?.until ?? 0) };
  } catch {
    return { fails: 0, level: 0, until: 0 };
  }
}

function writeLock(l: GateLock) {
  try {
    localStorage.setItem(LOCK_KEY, JSON.stringify(l));
  } catch {
    /* ignore */
  }
}

/** A wrong answer: returns the new lock state (until > now = locked out). */
export function recordFail(now = Date.now()): GateLock {
  const cur = readLock();
  const fails = cur.fails + 1;
  if (fails >= GATE_MAX_TRIES) {
    const level = cur.level + 1;
    const next = { fails: 0, level, until: now + Math.min(LOCK_MAX_MS, LOCK_BASE_MS * 2 ** (level - 1)) };
    writeLock(next);
    return next;
  }
  const next = { ...cur, fails, until: 0 };
  writeLock(next);
  return next;
}

export function recordPass(): void {
  writeLock({ fails: 0, level: 0, until: 0 });
}

export interface GateChallenge {
  a: number;
  b: number;
  answer: number;
}

/** Two two-digit numbers whose ones digits carry (so the sum needs real adding), 30..149. */
export function makeChallenge(rand = Math.random): GateChallenge {
  for (;;) {
    const a = 21 + Math.floor(rand() * 68); // 21..88
    const b = 12 + Math.floor(rand() * 76); // 12..87
    if ((a % 10) + (b % 10) < 10 || a % 10 === 0 || b % 10 === 0 || a === b) continue;
    return { a, b, answer: a + b };
  }
}

const EN_ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const ZH_ONES = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
const VI_ONES = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];

/** 0..99 in words (EN "thirty-seven", VI "ba mươi bảy", zh "三十七"; other locales use English words for now). */
export function numberWords(n: number, lang: Lang): string {
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  if (lang === 'vi') {
    if (n < 10) return VI_ONES[n]!;
    if (tens === 1) return ones === 0 ? 'mười' : `mười ${ones === 5 ? 'lăm' : VI_ONES[ones]}`;
    const head = `${VI_ONES[tens]} mươi`;
    if (ones === 0) return head;
    return `${head} ${ones === 1 ? 'mốt' : ones === 5 ? 'lăm' : ones === 4 ? 'tư' : VI_ONES[ones]}`;
  }
  if (lang === 'zh-Hans' || lang === 'zh-Hant' || lang === 'ja') {
    if (n < 10) return ZH_ONES[n]!;
    return `${tens > 1 ? ZH_ONES[tens] : ''}十${ones ? ZH_ONES[ones] : ''}`;
  }
  if (n < 20) return EN_ONES[n]!;
  return ones ? `${EN_TENS[tens]}-${EN_ONES[ones]}` : EN_TENS[tens]!;
}
